<template>
  <div :class="'table-responsive' + (config.actions?.buttons.sticky ? ' sticky-action-buttons' : '')">
    <div v-if="config.adjustments.bar && Object.keys(getActiveFilters()).length" class="adjustments-filters pb-2">
      Aktive Filter:&nbsp;
      <Link
        v-for="(value, key) in getActiveFilters()"
        :key="key"
        :href="route(`${routePrefix}.${resourceType}.adjust`, paramsWithoutFilter(key))"
        class="rounded-pill border border-primary px-2 position-relative hover-danger"
      >
        <span style="white-space: nowrap">{{ key }}: {{ value }}</span>
        &nbsp;
        <i class="bi bi-x small position-absolute top-0 end-0 pe-1 pt-0"></i>
      </Link>
    </div>
    <table class="table table-sm forgeron-model-table">
      <thead>
        <tr class="forgeron-table-row forgeron-table-header">
          <th v-if="hasBulkEdit()" class="align-bottom forgeron-table-column" @click="toggleAll(!selectedAll)">
            <input :checked="selectedAll ?? false" type="checkbox"/>
          </th>
          <th
            v-if="config.adjustments.bar && !localMode.includes('component')"
            class="align-top pt-0 forgeron-table-column"
          >
            <div class="d-flex">
              <div class="flex-grow-1 pt-1">
                <div class="pt-2">
                  <span class="column-name">ID</span>
                </div>
              </div>
              <div class="flex-grow-0">
                <Link
                  :href="route(`${routePrefix}.${resourceType}.adjust`, getIndexParams('id'))"
                  class="px-1 sort-link h-100 pt-1 d-inline-block text-muted"
                >
                  <i
                    :class="
                      'bi bi-2x bi-sort-' +
                      (sort === 'id' ? 'down-alt' : 'down') +
                      (sort === 'id' || sort === '-' + 'id' ? ' active' : '')
                    "
                    style="font-size: 1.5rem"
                  ></i>
                </Link>
              </div>
            </div>
          </th>
          <th
            v-for="(attributeType, attribute) in resource.meta.listed"
            :key="attribute"
            class="align-top pt-0 forgeron-table-column"
            :class="'forgeron-attribute-header-' + attribute"
            scope="col"
          >
            <column-header
              :column="attribute"
              :config="config"
              :exclude-null="localExcludeNull"
              :filter="localFilter"
              :meta="meta"
              :options="options"
              :params="getIndexParams(attribute)"
              :plain="!config.adjustments.bar"
              :resource="resource"
              :resource-type="resourceType"
              :sort="sort"
              @update:params="updateParams"
            ></column-header>
          </th>
          <th
            :style="config.actions?.buttons.sticky ? 'min-width: ' + (localMode === 'full' ? 9 : 4) + 'rem' : ''"
            class="align-top p-0 forgeron-table-column"
            style="text-align: right; padding-right: 0.75rem"
          >
            <template v-if="$page.url !== `/admin/${resourceType}?view_deleted`">
              <button
                v-if="bulkSelected()"
                class="btn-icon text-primary pe-2 me-1"
                style="background-color: transparent; border: none"
                type="button"
                @click="bulkEdit(route(`${routePrefix}.${resourceType}.bulk-edit`))"
              >
                <i class="bi bi-pencil-square" style="cursor: pointer; font-size: 1.5625rem"></i>
              </button>
              <button
                v-if="bulkSelected() && config.actions?.models.delete"
                class="btn-icon text-primary pe-2 me-1"
                style="background-color: transparent; border: none"
                type="button"
                @click="bulkDelete(route(`${routePrefix}.${resourceType}.bulk-delete`), trans)"
              >
                <i class="bi bi-trash" style="cursor: pointer; font-size: 1.5625rem"></i>
              </button>
              <a
                v-if="config.actions?.models.export"
                :href="route(`${routePrefix}.${resourceType}.export`, getIndexParams('id'))"
                class="pe-2 me-2"
              >
                <i class="fa-solid fa-download" style="cursor: pointer; font-size: 1.4rem"></i>
              </a>
              <template v-if="config.actions?.models.create">
                <Link
                  v-if="localMode === 'full'"
                  :href="route(`${routePrefix}.${resourceType}.create`, { resourceType })"
                >
                  <i class="bi bi-plus-square" style="cursor: pointer; font-size: 1.5rem"></i>
                </Link>
                <!--              <Link-->
                <!--                v-if="localMode === 'dashboard-component' && resource.meta.config.component.requestType === 'inertia'"-->
                <!--                :href="route(`${routePrefix}.${resourceType}.adjust`, getIndexParams(null, 0))"-->
                <!--              >-->
                <!--                <i class="bi bi-plus-square" style="cursor: pointer; font-size: 1.5rem"></i>-->
                <!--              </Link>-->
                <resource-action
                  v-if="localMode === 'dashboard-component' && meta.relationInfo.relationType !== 'belongsToMany'"
                  :action="'pick'"
                  :action-data="{
                    iconClass: 'bi bi-plus-square',
                    target: route(`${routePrefix}.${options.parentResource.meta.resourceType}.adjust`, {
                      id: resource.data.id,
                      picked: null,
                      relation: '',
                    }),
                    relation: resource.meta.relationInfo?.name,
                  }"
                  :model="null"
                  :options="options"
                  :resource-type="resourceType"
                  class="py-0 px-2"
                  @updatePicked="updatePicked"
                ></resource-action>
                <Link v-if="localMode === 'dashboard'" :href="route(`${routePrefix}.${resourceType}.dashboard`)">
                  <i class="bi bi-plus-square" style="cursor: pointer; font-size: 1.5rem"></i>
                </Link>
              </template>
            </template>
          </th>
        </tr>
      </thead>
      <tbody v-if="Object.values(models.data).length > 0">
        <tr
          v-for="(model, key) in models.data"
          :key="key"
          :class="selectedModels[model.data.id] || selectedAll ? 'bg-info bg-opacity-10' : ''"
          :style="getStyles(model, 'row')"
          class="forgeron-table-row"
          @click="toggleSelected(model.data.id, !selectedModels[model.data.id], $event)"
        >
          <th v-if="hasBulkEdit()" class="align-top forgeron-table-column">
            <input
              :checked="(selectedModels[model.data.id] || selectedAll) ?? false"
              :disabled="selectedAll"
              type="checkbox"
            />
          </th>
          <td v-if="config.adjustments.bar && !localMode.includes('component')" class="forgeron-table-column">
            {{ model.data.id }}
          </td>
          <td
            v-for="(attributeType, attribute) in resource.meta.listed"
            :key="attribute"
            :class="'forgeron-table-column forgeron-attribute-' + attribute"
            :style="getStyles(model, attribute)"
            class="text-truncate"
          >
            <syncing-input
              v-if="meta.mutations[attribute] === 'reactive'"
              :resource="model"
              :column-type="meta.elementTypes[attribute] ?? 'varchar'"
              :attribute="attribute"
              :options="options"
            ></syncing-input>

            <property-changelist
              v-else-if="meta.elementTypes[attribute] === 'property-changelist'"
              :properties="model.data.attributes[attribute]"
              :resource-type="model.data.attributes.subject_meta.resourceType"
              :meta="model.data.attributes.subject_meta"
              :event="model.data.attributes.event"
            ></property-changelist>

            <presentation-field
              v-else-if="meta.elementTypes[attribute]['collection'] != 'relation-select'"
              :id="model.data.id"
              :column="attribute"
              :column-type="meta.elementTypes[attribute] ?? 'varchar'"
              :element="getElement(attribute, 'lists', meta)"
              :image-url="model.data.attributes.image_url ?? null"
              :meta="meta"
              :mutation="meta.mutations[attribute] ?? null"
              :resource-type="resourceType"
              :show-id="$page.props.user.roles.includes('tech')"
              :value="model.data.attributes[attribute] ?? model.data.relations[attribute]"
              @add-toast="$emit('addToast', $event)"
              @update:data="(data) => $emit('update:data', data)"
            ></presentation-field>

            <relation-field
              v-else-if="meta.elementTypes[attribute]['collection'] == 'relation-select'"
              :id="resourceType + '-' + attribute"
              :label="getLabel(model, attribute, resourceType, trans)"
              :name="attribute"
              :options="options"
              :relation-title="model.data.relations[attribute].data.attributes?.model_title"
              :relation-type="model.data.relations[attribute].resourceType"
              :value="model.data.attributes[`${attribute}_id`]"
              @update:data="
                (foreignId, relation, title) =>
                  $emit('update:relation', { relation, id: model.data.id, foreignId, title })
              "
            ></relation-field>
          </td>
          <td
            v-if="$page.url === `/admin/${resourceType}?view_deleted`"
            class="align-center p-1 forgeron-table-column"
            style="text-align: right"
          >
            <Link :href="route(`${routePrefix}.${resourceType}.restore`, model.data.id)" class="py-0 px-2">
              <i class="bi bi-arrow-counterclockwise me-1"></i
              >{{ trans(`models.${resourceType}.name.singular`) }} wiederherstellen
            </Link>
          </td>
          <td
            v-else-if="getActionButtons(model, route, routePrefix)"
            class="align-center p-1 forgeron-table-column model-actions"
            style="text-align: right; font-size: 1.25rem; white-space: nowrap"
          >
            <button
              v-if="resource.meta.relationInfo?.relationType == 'belongsToMany' && localMode === 'dashboard-component'"
              class="btn-icon py-0 px-2"
              style="
                background-color: transparent;
                border: none;
                cursor: pointer;
                white-space: nowrap;
                color: var(--link-color);
              "
              type="button"
              @click="$emit('detach', model)"
            >
              <i class="bi bi-trash" style="font-size: 1rem; color: #ff0000"></i>
            </button>
            <template v-if="model.data.id">
              <resource-action
                v-for="(actionData, action) in getActionButtons(model, route, routePrefix)"
                :key="action"
                :action="action"
                :action-data="actionData"
                :model="model"
                :options="options"
                :resource-type="resourceType"
                class="py-0 px-2"
                @updatePicked="updatePicked"
              ></resource-action>
            </template>
          </td>
        </tr>
      </tbody>
      <tbody v-if="!Object.values(models.data).length">
        <tr class="forgeron-table-row">
          <td :colspan="meta.attributes.length + 3" class="forgeron-table-column">keine Einträge vorhanden</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div v-if="models.page && localPagination && models.page.last_page > 1" class="mt-3">
    <pagination
      :name="trans(`models.${resourceType}.name.plural`)"
      :page="models.page"
      :resource="models"
      :steps="[config.adjustments.perPage]"
      @trigger:refresh="(data, page) => $emit('trigger:refresh', data, page)"
      @update:perPage="updatePerPage"
    ></pagination>
  </div>
</template>

<script>
import ColumnHeader from '@kanrisha/components/Collection/List/ColumnHeader';
import Pagination from '@kanrisha/components/Pagination';
import PresentationField from '@kanrisha/components/Presentation/PresentationField';
import { Link, router } from '@inertiajs/vue3';
import useActionButtons from '@kanrisha/composables/useActionButtons';
import useBulkSelect from '@kanrisha/composables/useBulkSelect';
import usePagination from '@kanrisha/composables/usePagination';
import useResourceParams from '@kanrisha/composables/useResourceParams';
import useResource from '@kanrisha/composables/useResource';
import RelationField from '@kanrisha/components/Form/RelationField.vue';
import ResourceAction from '@kanrisha/components/Links/ResourceAction';
import { ref } from 'vue';
import useModels from '../../composables/useModels';
import SyncingInput from '@kanrisha/components/Elements/Sync/SyncingInput';
import PropertyChangelist from '@kanrisha/components/Elements/EventLog/PropertyChangelist';

export default {
  components: {
    ColumnHeader,
    Link,
    Pagination,
    PresentationField,
    PropertyChangelist,
    RelationField,
    ResourceAction,
    SyncingInput,
  },
  props: {
    options: {
      type: Object,
      default: () => ({
        parentResource: null,
      }),
    },
    resource: { type: Object, default: () => {} },
    // if set, this will be used instead of resource.meta
    // do not remove this or collection items will throw errors as those do not have own meta for better performance
    meta: { type: Object, default: () => null },
    mode: { type: String, default: 'component' }, // deprecated
    name: { type: String, default: 'default' },
    pagination: { type: Boolean, default: () => true }, // deprecated: this should go on options or config
  },
  emits: ['addToast', 'updatePicked', 'update:data', 'update:relation', 'trigger:refresh', 'detach'],
  setup(props, { emit }) {
    const {
      getActiveFilters,
      getIndexParams,
      getModelParams,
      localExcludeNull,
      localFilter,
      paramsWithoutFilter,
      updateParams,
    } = useResourceParams(props);

    const { adjustments, attributes, config, getElement, resourceType } = useResource(props, props.resource);
    const { selectedModels, selectedAll, toggleAll, toggleSelected, bulkSelected, bulkDelete, bulkEdit, hasBulkEdit } =
      useBulkSelect(props);
    const { getActionButtons } = useActionButtons(props, bulkSelected);
    const { updatePerPage } = usePagination();
    const localPagination = ref(props.options.pagination ?? props.pagination);
    const models = ref(props.resource);
    const { pickModel } = useModels({ resourceType: resourceType.value });

    const localMode = ref(config.value.mode ?? props.mode); // fixme: reverse this, direct mode should win
    const sort = ref(props.resource.meta.adjustments.sort);

    // todo: check route
    const confirmDeletion = (event) => {
      if (!confirm(event.target.dataset.confirm)) {
        return;
      }

      router.delete(event.target.dataset.url, {}, { preserveState: true, only: ['models'], replace: true });
    };

    const getLabel = (model, attribute, resourceType, trans) => {
      let meta = props.meta ?? model.meta;

      return meta.labels[attribute] ?? trans(`models.${resourceType}.relations.${attribute}`);
    };

    const getStyles = (model, attribute) => {
      let style = '';
      let meta = props.meta ?? model.meta;

      if (meta.colWidths && meta.colWidths[attribute]) {
        style = `width: ${meta.colWidths[attribute]}px; `;
      } else {
        style = 'max-width: 10rem; ';
      }

      if (!config.value.decoration?.items[attribute]) {
        return style;
      }

      let background = config.value.decoration?.items[attribute][0] ?? 'inherit';
      let color = config.value.decoration?.items[attribute][1] ?? 'inherit';

      style = style + `background: ${model.data.attributes[background]}; color: ${model.data.attributes[color]}`;

      return style;
    };

    const updatePicked = async (picked = null) => {
      let pickedModel;
      if (picked) {
        pickedModel = await pickModel(
          props.options.parentResource.data.id,
          props.meta.relationInfo.parentType,
          props.meta.relationInfo.name,
          picked.data.id
        );
      } else {
        pickedModel = JSON.parse(JSON.stringify(props.resource.dummy));
      }

      emit('updatePicked', { name: props.meta.relationInfo.name, model: pickedModel ?? null });
    };

    return {
      adjustments,
      attributes,
      bulkDelete,
      bulkEdit,
      bulkSelected,
      config,
      confirmDeletion,
      getActionButtons,
      getActiveFilters,
      getElement,
      getIndexParams,
      getLabel,
      getModelParams,
      getStyles,
      hasBulkEdit,
      localFilter,
      localExcludeNull,
      localMode,
      localPagination,
      models,
      paramsWithoutFilter,
      resourceType,
      selectedAll,
      selectedModels,
      sort,
      toggleAll,
      toggleSelected,
      updateParams,
      updatePerPage,
      updatePicked,
    };
  },
};
</script>
